/* eslint-disable sonarjs/new-cap */
import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { saveUserDeviceInfo } from 'api/user-device-info'
import constate from 'constate'
import { getBrowserName, getBrowserVersion } from 'helpers'
import { useEffect } from 'react'

export type UserDeviceInfoContextType = {
  userDeviceInfo: UseQueryResult<unknown, unknown>
}

/**
 * UserDeviceInfoProvider component collects and saves user device information.
 * It uses the react-query library to handle asynchronous data fetching and caching.
 *
 * @param {Props} props - The children components that will have access to the UserDeviceInfoContext.
 * @returns {JSX.Element} The provider component that wraps around the children components.
 */
export const useUserDeviceInfo = () => {
  const userDeviceInfo = useQuery({
    queryKey: ['userDeviceInfo'],
    queryFn: async () => {
      const data = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        browser: {
          name: getBrowserName(navigator.userAgent),
          version: getBrowserVersion(navigator.userAgent),
        },
        screen: {
          width: screen.width,
          height: screen.height,
          colorDepth: screen.colorDepth,
        },
        window: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        cookiesEnabled: navigator.cookieEnabled,
        javaEnabled: navigator.javaEnabled(),
        online: navigator.onLine,
        referrer: document.referrer,
      }
      await saveUserDeviceInfo(data)
      localStorage.setItem('userDeviceInfoSaved', 'true')
      return data
    },
    enabled: !localStorage.getItem('userDeviceInfoSaved'),
  })

  useEffect(() => {
    const cleanup = () => {
      localStorage.removeItem('userDeviceInfoSaved')
    }
    window.addEventListener('beforeunload', cleanup)
    return () => {
      window.removeEventListener('beforeunload', cleanup)
    }
  }, [])

  return { userDeviceInfo } as const
}

export const [UserDeviceInfoProvider, useUserDeviceInfoContext] =
  constate(useUserDeviceInfo)
